import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import UserAPI from "../services/UserAPI";
import "./css/Consent.css";

/**
 * Component for handling phone verification
 * This component allows users to enter the SMS verification code they received
 */
function VerifyPhone() {
  const [searchParams] = useSearchParams();
  const userUuid = searchParams.get("userUuid");
  const phone = searchParams.get("phone");

  const [formData, setFormData] = useState({
    verificationCode: "",
    phoneNumber: phone || "",
  });

  const [status, setStatus] = useState({
    isLoading: false,
    isVerified: false,
    message: "",
    error: "",
  });

  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userUuid) {
      setStatus({
        isLoading: false,
        isVerified: false,
        message: "",
        error:
          "No user information provided. Please try creating your account again.",
      });
    }
  }, [userUuid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (status.error) {
      setStatus((prev) => ({ ...prev, error: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.verificationCode.trim()) {
      setStatus((prev) => ({
        ...prev,
        error: "Please enter the verification code.",
      }));
      return;
    }

    if (!userUuid) {
      setStatus((prev) => ({
        ...prev,
        error: "User information is missing. Please try again.",
      }));
      return;
    }

    setStatus((prev) => ({ ...prev, isLoading: true, error: "" }));

    try {
      console.log(
        `[VerifyPhone] Attempting to verify phone for user: ${userUuid}`
      );

      const response = await UserAPI.verifyUserPhone(userUuid, {
        verificationCode: formData.verificationCode,
      });

      console.log("Phone verification response:", response);

      if (response && response.success && response.verified) {
        setStatus({
          isLoading: false,
          isVerified: true,
          message: response.fullyVerified
            ? "Phone verification successful! Your account is now fully verified and you can log in."
            : "Phone verification successful! Please also verify your email address to complete your account setup.",
          error: "",
        });

        // Redirect to login after a short delay if fully verified
        if (response.fullyVerified) {
          setTimeout(() => {
            navigate(
              "/login?message=" +
                encodeURIComponent(
                  "Account fully verified! You can now log in."
                )
            );
          }, 3000);
        }
      } else {
        setStatus({
          isLoading: false,
          isVerified: false,
          message: "",
          error:
            response?.message || "Invalid verification code. Please try again.",
        });
      }
    } catch (error) {
      console.error("Phone verification error:", error);
      setStatus({
        isLoading: false,
        isVerified: false,
        message: "",
        error:
          error.response?.data?.message ||
          "Verification failed. Please try again.",
      });
    }
  };

  const handleResendCode = async () => {
    if (!userUuid) {
      setStatus((prev) => ({ ...prev, error: "User information is missing." }));
      return;
    }

    setIsResending(true);
    setStatus((prev) => ({ ...prev, error: "" }));

    try {
      console.log(
        `[VerifyPhone] Resending verification code for user: ${userUuid}`
      );

      const response = await UserAPI.sendPhoneVerification(userUuid);

      if (response && response.success) {
        setStatus((prev) => ({
          ...prev,
          message:
            "Verification code resent! Please check your phone for a new SMS message.",
        }));
      } else {
        setStatus((prev) => ({
          ...prev,
          error:
            response?.message ||
            "Failed to resend verification code. Please try again.",
        }));
      }
    } catch (error) {
      console.error("Resend verification error:", error);
      setStatus((prev) => ({
        ...prev,
        error:
          error.response?.data?.message ||
          "Failed to resend verification code. Please try again.",
      }));
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToLogin = () => {
    navigate("/login");
  };

  if (status.isVerified) {
    return (
      <div className="consent-container">
        <div className="consent-content verification-success">
          <div className="success-icon">✅</div>
          <h2>Phone Verification Successful!</h2>
          <p className="success-message">{status.message}</p>
          <button
            className="consent-button primary-button"
            onClick={handleGoToLogin}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="consent-container">
      <div className="consent-content">
        <h2>Verify Your Phone Number</h2>
        <p className="verification-instructions">
          We've sent a verification code to your phone number. Please enter the
          code below to complete your phone verification.
        </p>

        {phone && (
          <p className="phone-display">
            <strong>Phone:</strong> {phone}
          </p>
        )}

        <form onSubmit={handleSubmit} className="verification-form">
          <div className="form-group">
            <label htmlFor="verificationCode">Verification Code</label>
            <input
              type="text"
              id="verificationCode"
              name="verificationCode"
              value={formData.verificationCode}
              onChange={handleChange}
              placeholder="Enter 6-digit code"
              maxLength="6"
              pattern="[0-9]{6}"
              className="verification-code-input"
              disabled={status.isLoading}
            />
          </div>

          {status.error && <div className="error-message">{status.error}</div>}

          {status.message && (
            <div className="success-message">{status.message}</div>
          )}

          <div className="verification-actions">
            <button
              type="submit"
              className="consent-button primary-button"
              disabled={status.isLoading}
            >
              {status.isLoading ? "Verifying..." : "Verify Phone"}
            </button>

            <button
              type="button"
              className="consent-button secondary-button"
              onClick={handleResendCode}
              disabled={isResending || status.isLoading}
            >
              {isResending ? "Resending..." : "Resend Code"}
            </button>
          </div>
        </form>

        <div className="help-section">
          <p className="help-text">
            Didn't receive the code? Check that your phone can receive SMS
            messages and try resending.
          </p>
          <p className="help-text">
            <button
              type="button"
              className="link-button"
              onClick={handleGoToLogin}
            >
              Back to Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default VerifyPhone;
